import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Radio,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import SearchIcon from '@mui/icons-material/Search';

import config from '../config';
import { Button, EmptyState, LoadingState } from '../components/ui';
import { toast } from '../components/Toast';
import { apiPost } from '../utils/apiAuth';
import {
  isSelectionCorrect,
  normalizeQuestion,
  parseTestingRoute,
  prepareContentCreationNavigation,
  buildTopicSubtopicLookup,
  enrichQuestionWithNames,
  searchTestingQuestions,
} from '../utils/mcqTesting';
import { getTopicsSubtopicsBySubject } from '../utils/topicsSubtopicsCache';

const QUESTION_TITLE_PREVIEW_LENGTH = 120;

function truncateQuestionTitle(text, maxLength = QUESTION_TITLE_PREVIEW_LENGTH) {
  const value = String(text || '').trim();
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}…`;
}

export function McqTestingCard({
  question,
  index,
  mode,
  selectedOptionIds,
  result,
  onSelectionChange,
  onEditQuestion,
}) {
  const showFeedback = mode === 'answers' || result !== undefined;
  const isCorrect = result === true;
  const topicLabel = question.topicName || question.topicId || '';
  const subTopicLabel = question.subTopicName || question.subTopicId || '';

  const selectOption = (optionId) => {
    if (question.multiple) {
      const nextSelection = selectedOptionIds.includes(optionId)
        ? selectedOptionIds.filter((id) => id !== optionId)
        : [...selectedOptionIds, optionId];
      onSelectionChange(nextSelection);
      return;
    }
    onSelectionChange([optionId]);
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={0.5}
        sx={{ mb: 1 }}
      >
        <Typography
          fontWeight={700}
          sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
        >
          {[topicLabel, subTopicLabel].filter(Boolean).join(' › ') || 'Untitled topic'}
        </Typography>
        {onEditQuestion ? (
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onEditQuestion(question);
            }}
            sx={{
              fontFamily: 'monospace',
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'left',
              flexShrink: 0,
            }}
          >
            {question.id}
          </Link>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: 'monospace', flexShrink: 0 }}
          >
            {question.id}
          </Typography>
        )}
      </Stack>
      <Paper
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: 1.5,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          <Box
            component="span"
            sx={{ fontWeight: 700, color: 'primary.dark', mr: 0.75 }}
          >
            {index + 1}.
          </Box>
          {question.text}
        </Typography>
      </Paper>
      <Stack spacing={0.75}>
        {question.options.map((option, optionIndex) => {
          const selected = selectedOptionIds.includes(option.id);
          const correct = question.correctOptionIds.includes(option.id);
          const highlightCorrect = showFeedback && correct;
          const highlightWrong = result === false && selected && !correct;
          const Control = question.multiple ? Checkbox : Radio;
          const optionControlId = `mcq-${question.id}-${option.id}`;

          return (
            <Paper
              component="label"
              htmlFor={mode === 'answers' ? undefined : optionControlId}
              key={option.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                cursor: mode === 'answers' ? 'default' : 'pointer',
                border: '1px solid',
                borderWidth: selected || highlightCorrect || highlightWrong ? 2 : 1,
                borderColor: highlightCorrect
                  ? 'success.main'
                  : highlightWrong
                    ? 'error.main'
                    : selected
                      ? 'primary.main'
                      : 'divider',
                bgcolor: 'background.paper',
                borderRadius: 1.5,
                boxShadow: selected ? 'none' : '0 2px 4px rgba(0,0,0,0.05)',
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 1,
                  bgcolor: selected ? 'primary.main' : 'action.hover',
                  color: selected ? 'primary.contrastText' : 'text.secondary',
                  fontWeight: 600,
                }}
              >
                {String.fromCharCode(65 + optionIndex)}
              </Box>
              <Typography sx={{ flex: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {option.text}
              </Typography>
              <Control
                id={optionControlId}
                checked={selected}
                onChange={() => selectOption(option.id)}
                disabled={mode === 'answers'}
                inputProps={{ 'aria-label': option.text }}
              />
            </Paper>
          );
        })}
      </Stack>

      {result !== undefined && (
        <Alert severity={isCorrect ? 'success' : 'error'} sx={{ mt: 2 }}>
          {isCorrect ? 'Correct answer.' : 'Incorrect answer.'}
        </Alert>
      )}

      {showFeedback && question.explanation && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            Explanation
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
            {question.explanation}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function McqTestingPage({ routeQuestionType }) {
  const { subjectId, verified } = useParams();
  const navigate = useNavigate();
  const routeConfig = useMemo(
    () => parseTestingRoute(routeQuestionType, verified),
    [routeQuestionType, verified]
  );
  const [questions, setQuestions] = useState([]);
  const [malformedCount, setMalformedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('answers');
  const [selections, setSelections] = useState({});
  const [results, setResults] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchMatches, setSearchMatches] = useState([]);
  const activeQuestionNavRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAllQuestions = async () => {
      if (!routeConfig) {
        setError('The question type or verified value in this URL is invalid.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        let page = 1;
        let totalPages = 1;
        const rows = [];

        const topicsPromise = getTopicsSubtopicsBySubject(subjectId).catch(() => []);

        do {
          const response = await apiPost(
            `${config.API_BASE_URL}/api/get-questions-hierarchical/`,
            {
              subject_id: subjectId,
              question_type: routeConfig.apiQuestionType,
              validate: routeConfig.verified,
              page,
              limit: 100,
            }
          );
          const data = response.data || {};
          rows.push(...(data.questions || []));
          totalPages = Math.max(1, Number(data.total_pages) || 1);
          page += 1;
        } while (page <= totalPages);

        const topics = await topicsPromise;
        if (cancelled) return;
        const lookup = buildTopicSubtopicLookup(topics);
        const normalized = rows
          .map((record) => normalizeQuestion(record, routeConfig.apiQuestionType))
          .filter(Boolean)
          .map((question) => enrichQuestionWithNames(question, lookup));
        setQuestions(normalized);
        setMalformedCount(rows.length - normalized.length);
        setCurrentQuestionIndex(0);
      } catch (fetchError) {
        if (!cancelled) {
          setError(
            fetchError?.response?.data?.detail ||
              'Unable to load questions. Please try again.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAllQuestions();
    return () => {
      cancelled = true;
    };
  }, [routeConfig, subjectId]);

  useEffect(() => {
    activeQuestionNavRef.current?.scrollIntoView({
      block: 'center',
      inline: 'nearest',
    });
  }, [currentQuestionIndex]);

  const setQuestionSelection = (questionId, optionIds) => {
    setSelections((current) => ({ ...current, [questionId]: optionIds }));
    setResults((current) => {
      const next = { ...current };
      delete next[questionId];
      return next;
    });
  };

  const submitQuestion = (question) => {
    setResults((current) => ({
      ...current,
      [question.id]: isSelectionCorrect(
        selections[question.id] || [],
        question.correctOptionIds
      ),
    }));
  };

  const changeMode = (_event, nextMode) => {
    if (!nextMode) return;
    setMode(nextMode);
    setResults({});
  };

  const jumpToQuestion = (question) => {
    const index = questions.findIndex((item) => item.id === question.id);
    if (index < 0) return;
    setCurrentQuestionIndex(index);
    setSearchModalOpen(false);
  };

  const runSearch = () => {
    const matches = searchTestingQuestions(questions, searchQuery);
    if (!String(searchQuery || '').trim()) {
      toast.warning('Enter text to search.');
      return;
    }
    if (matches.length === 0) {
      toast.warning('No matching questions');
      setSearchModalOpen(false);
      setSearchMatches([]);
      return;
    }
    if (matches.length === 1) {
      jumpToQuestion(matches[0]);
      return;
    }
    setSearchMatches(matches);
    setSearchModalOpen(true);
  };

  const openQuestionInEditor = (question) => {
    try {
      if (!question?.subTopicId || !question?.topicId) {
        toast.warning('Unable to resolve topic/subtopic for this question.');
        return;
      }

      let subjectName = subjectId;
      try {
        const storedName = sessionStorage.getItem('contentCreation_subjectName');
        if (storedName) {
          const parsed = JSON.parse(storedName);
          if (parsed) subjectName = parsed;
        }
      } catch {
        // keep subjectId fallback
      }

      const returnPath = `/testing/${routeConfig.apiQuestionType}/${subjectId}/${
        routeConfig.verified ? '1' : '0'
      }`;
      const navigationData = prepareContentCreationNavigation({
        question,
        contentType: routeConfig.apiQuestionType,
        subjectId,
        subjectName,
        returnPath,
      });

      navigate('/content-creation', {
        state: { data: navigationData },
      });
    } catch (navError) {
      toast.error('Unable to open question in content creation.');
    }
  };

  const currentQuestion = questions[currentQuestionIndex];

  if (loading) {
    return <LoadingState message="Loading all questions…" sx={{ minHeight: 320 }} />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: { xs: 'auto', md: '100vh' },
        minHeight: 0,
        pt: 1,
        pb: { xs: 1, md: 0 },
        px: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        boxSizing: 'border-box',
      }}
    >
      <Paper
        elevation={1}
        sx={{ p: 1.5, mb: 1, flexShrink: 0, borderRadius: 2 }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <QuizOutlinedIcon color="primary" />
              <Typography variant="h5" fontWeight={700}>
                {routeConfig?.apiQuestionType === 'quiz' ? 'Quiz' : 'MCQ'} Testing
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Subject {subjectId} · {routeConfig?.verified ? 'Verified' : 'Not Verified'} ·{' '}
              {questions.length} questions
            </Typography>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1.5}
          >
            <TextField
              size="small"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  runSearch();
                }
              }}
              placeholder="Search question, options, topic…"
              inputProps={{ 'aria-label': 'Search questions' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Run search"
                      edge="end"
                      onClick={runSearch}
                      size="small"
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { sm: 260 } }}
            />
            <ToggleButtonGroup
              exclusive
              value={mode}
              onChange={changeMode}
              size="small"
              aria-label="Testing mode"
            >
              <ToggleButton value="answers">Show correct answers</ToggleButton>
              <ToggleButton value="practice">Practice &amp; Submit</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {malformedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {malformedCount} malformed question{malformedCount === 1 ? '' : 's'} could not be displayed.
        </Alert>
      )}

      {!error && questions.length === 0 ? (
        <EmptyState
          icon={CheckCircleOutlineIcon}
          title="No matching questions"
          description="No questions match this subject, type, and verified status."
        />
      ) : currentQuestion ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 1,
            flex: 1,
            minHeight: 0,
            overflow: { xs: 'visible', md: 'hidden' },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              width: 80,
              flexShrink: 0,
              py: 0.5,
              borderRadius: 0,
              bgcolor: '#f8f9fa',
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'center',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              {questions.map((question, index) => {
                const active = index === currentQuestionIndex;
                const answered = results[question.id] !== undefined;
                return (
                  <Box
                    component="button"
                    type="button"
                    key={question.id}
                    ref={active ? activeQuestionNavRef : undefined}
                    aria-label={`Go to question ${index + 1}`}
                    onClick={() => setCurrentQuestionIndex(index)}
                    sx={{
                      width: 40,
                      minWidth: 40,
                      height: 40,
                      flexShrink: 0,
                      p: 0,
                      borderRadius: '5px',
                      border: '1px solid',
                      borderColor: active
                        ? '#003bb3'
                        : answered
                          ? '#00663d'
                          : '#9ca3af',
                      bgcolor: active
                        ? '#e8edff'
                        : answered
                          ? '#00663d'
                          : 'transparent',
                      color: active ? '#003bb3' : answered ? '#90ee90' : '#4a5568',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1,
                      boxShadow: 'none',
                    }}
                  >
                    {index + 1}
                  </Box>
                );
              })}
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              minWidth: 0,
              p: { xs: 1.5, md: 2 },
              bgcolor: '#f8f9fa',
              borderRadius: 2,
              overflowY: { xs: 'visible', md: 'auto' },
              overscrollBehavior: 'contain',
            }}
          >
            <McqTestingCard
              question={currentQuestion}
              index={currentQuestionIndex}
              mode={mode}
              selectedOptionIds={selections[currentQuestion.id] || []}
              result={results[currentQuestion.id]}
              onSelectionChange={(optionIds) =>
                setQuestionSelection(currentQuestion.id, optionIds)
              }
              onEditQuestion={openQuestionInEditor}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                disabled={currentQuestionIndex === 0}
                onClick={() => setCurrentQuestionIndex((index) => index - 1)}
              >
                Previous
              </Button>
              <Typography variant="body2" color="text.secondary">
                {currentQuestionIndex + 1} of {questions.length}
              </Typography>
              <Stack direction="row" spacing={1}>
                {mode === 'practice' && (
                  <Button
                    disabled={!selections[currentQuestion.id]?.length}
                    onClick={() => submitQuestion(currentQuestion)}
                  >
                    Submit
                  </Button>
                )}
                <Button
                  disabled={currentQuestionIndex >= questions.length - 1}
                  onClick={() => setCurrentQuestionIndex((index) => index + 1)}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      ) : null}

      <Dialog
        open={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="testing-search-results-title"
      >
        <DialogTitle id="testing-search-results-title">
          Search results ({searchMatches.length})
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List disablePadding>
            {searchMatches.map((question) => {
              const listIndex = questions.findIndex((item) => item.id === question.id);
              return (
                <ListItemButton
                  key={question.id}
                  onClick={() => jumpToQuestion(question)}
                  alignItems="flex-start"
                >
                  <ListItemText
                    primary={
                      <Typography
                        component="span"
                        sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                      >
                        {listIndex >= 0 ? `${listIndex + 1}. ` : ''}
                        {question.id}
                      </Typography>
                    }
                    secondary={truncateQuestionTitle(question.text)}
                    secondaryTypographyProps={{ sx: { whiteSpace: 'pre-wrap' } }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
